import { action, computed, observable } from "mobx";

import { IStoreLifeCycle } from "@interface";

import { LMTWidgetElement } from "@data-types/live-widget/LMTWidgetElement";
import { LMTConfigParams } from "@data-types/live-widget/LMTConfigParams";
import { WidgetLocation } from "@data-types/live-widget/WidgetLocation";
import { ProviderNameEnum } from "@gp/models";
import { LiveStreamWidget } from "@state/stores/live-widgets/LiveStreamWidget";
import { LiveMatchTrackerState } from "./LiveMatchTrackerState";
import { LiveStreamState } from "./LiveStreamState";

import { isLMTWidget } from "./isLMTWidget";
import { isLSWidget } from "./isLSWidget";
import { TWidgetConfig } from "@data-types/live-widget/TWidgetConfig";
import { TLiveStreamWidgetConfig } from "@data-types/live-widget/TLiveStreamWidgetConfig";
import { LiveWidgetTypes } from "@data-types/live-widget/LiveWidgetTypes";

export class LiveWidgetStore implements IStoreLifeCycle {
	protected lmtStore: LiveMatchTrackerState;
	protected liveStreamStore: LiveStreamState;

	@observable public eventsWithLsError: Set<string> = new Set<string>();
	@observable public isStoreInitialized: boolean = false;
	@observable public widgetsAreSuspended: boolean = false;

	//#region  constructor

	constructor() {
		this.lmtStore = new LiveMatchTrackerState();
		this.liveStreamStore = new LiveStreamState();
	}

	@action.bound
	public onInitialize(): void {
		this.lmtStore.onInitialize();
		this.liveStreamStore.onInitialize();
		this.isStoreInitialized = true;
	}

	//#endregion constructor

	//#region unmount widgets

	/**
	 * Widgets remain in memory but will not be mounted to DOM.
	 * This is to stop network traffic due to inactivity.
	 * @param suspend indicates if widgets are to be suspended.
	 */
	@action.bound
	setWidgetSuspend(suspend: boolean) {
		if (this.widgetsAreSuspended === suspend) return;
		this.widgetsAreSuspended = suspend;
	}

	//#endregion unmount widget

	//#region any widget type

	/** Returns function that returns boolean that will tell if there is any active widget on location for given event id. */
	@computed public get findActiveWidget() {
		return (eventId: string, location: WidgetLocation) =>
			this.findActiveLmtWidget(eventId, location) ||
			this.findActiveLiveStreamWidget(eventId, location);
	}

	/** Returns true if there is any active widget on location SIDE */
	@computed public get hasActiveSideWidget(): boolean {
		return this.activeSideWidget != null;
	}

	/** Returns active side widget. */
	@computed public get activeSideWidget() {
		return (
			this.liveStreamStore.activeSideWidget ||
			this.lmtStore.activeSideWidget
		);
	}

	/** Pin side widget. */
	@action.bound
	public toggleWidgetPin(eventId: string, location: WidgetLocation): void {
		this.lmtStore.toggleWidgetPin(eventId, location);
		this.liveStreamStore.toggleWidgetPin(eventId, location);
	}

	@action.bound
	public updateWidget(widget: LiveStreamWidget | LMTWidgetElement) {
		if (isLMTWidget(widget)) {
			this.lmtStore.updateItem(widget);
		} else if (isLSWidget(widget)) {
			this.liveStreamStore.updateItem(widget);
		}
	}

	@action.bound
	public replaceWidgetMessage(
		widget: LiveStreamWidget | LMTWidgetElement,
		message: LiveStreamWidget["message"]
	) {
		if (isLMTWidget(widget)) {
			this.lmtStore.replaceWidgetMessage(widget);
		} else if (isLSWidget(widget)) {
			this.liveStreamStore.replaceWidgetMessage(widget, message);
		}
	}

	/**
	 * Adds ls if liveStreamStatus is "live".
	 * Else if adds lmt if matchId is not null.
	 * Else it does nothing.
	 */
	@action.bound
	public addWidget(config: TWidgetConfig): void {
		if (config.lsStatus === "live") {
			this.addLiveStreamWidget(config);
		} else {
			if (config.matchId == null || config.provider == null) {
				return;
			}
			this.addLmtWidget({
				...config,
				matchId: config.matchId,
				provider: config.provider,
			});
		}
	}

	/** Removes any widget on location for given eventId, or if location is SIDE remove for any event id */
	@action.bound
	removeWidget(eventId: string, location: WidgetLocation) {
		this.removeLiveStreamWidget(eventId, location);
		this.removeLmtWidget(eventId, location);
	}

	//#endregion any widget type

	//#region lmt

	/** All added lmt widgets */
	@computed public get activeLmtWidgets() {
		return this.lmtStore.activeWidgets;
	}

	/** Returns function that finds active widget for eventId on location  */
	@computed public get findActiveLmtWidget() {
		return this.lmtStore.findActiveWidget;
	}

	@action.bound
	public toggleLmtWidget(config: LMTConfigParams): void {
		if (config.location === WidgetLocation.SIDE) {
			this._toggleSideWidget(config, LiveWidgetTypes.LMT);
		} else {
			this.liveStreamStore.removeLiveStream(
				config.eventId,
				config.location
			);
			this.lmtStore.toggleLMTWidget(config);

			// Ensure only one LMT widget per event
			if (
				this.lmtStore.findActiveWidget(
					config.eventId,
					WidgetLocation.SIDE
				) &&
				this.lmtStore.findActiveWidget(
					config.eventId,
					WidgetLocation.EVENT_OFFER
				)
			) {
				this.lmtStore.removeLMTWidget(
					config.eventId,
					WidgetLocation.SIDE
				);
			}
		}
	}

	/** Adds lmt widget on location for event id and removes ls if it is present. */
	@action.bound
	public addLmtWidget(config: LMTConfigParams): void {
		if (
			config.location === WidgetLocation.SIDE &&
			this.activeSideWidget != null
		) {
			const prevEventId = this.activeSideWidget.eventId;

			const lmtWidget = this.lmtStore.findActiveWidget(
				prevEventId,
				WidgetLocation.SIDE
			);

			if (!lmtWidget || lmtWidget.provider != config.provider) {
				this.lmtStore.addLMTWidget(config);
			} else {
				this.lmtStore.updateItem(this.lmtStore.createLMT(config));
			}

			this.removeLiveStreamWidget(prevEventId, config.location);
			return;
		}
		this.removeLiveStreamWidget(config.eventId, config.location);
		this.lmtStore.addLMTWidget(config);
	}

	@action.bound
	private _toggleSideWidget(
		config: TLiveStreamWidgetConfig | LMTConfigParams,
		type: LiveWidgetTypes
	) {
		const prev = this.activeSideWidget;
		if (!prev) {
			if (type === LiveWidgetTypes.LS) {
				this.liveStreamStore.addLiveStream(config);
			} else {
				this.lmtStore.addLMTWidget(config as LMTConfigParams);
			}
			return;
		}

		const prevEventId = prev.eventId;
		const isSameEvent = prevEventId === config.eventId;

		if (type === LiveWidgetTypes.LS) {
			const exists = this.liveStreamStore.findActiveLiveStream(
				prevEventId,
				WidgetLocation.SIDE
			);
			if (!exists) {
				this.liveStreamStore.addLiveStream(config);
				this.lmtStore.removeLMTWidget(prevEventId, WidgetLocation.SIDE);
			} else {
				this.liveStreamStore.removeLiveStream(
					config.eventId,
					WidgetLocation.SIDE
				);
				if (!isSameEvent) {
					this.liveStreamStore.addLiveStream(config);
				}
			}
		} else {
			const exists = this.lmtStore.findActiveWidget(
				prevEventId,
				WidgetLocation.SIDE
			);
			if (!exists) {
				this.lmtStore.addLMTWidget(config as LMTConfigParams);
				this.liveStreamStore.removeLiveStream(
					prevEventId,
					WidgetLocation.SIDE
				);
			} else {
				this.lmtStore.removeLMTWidget(
					config.eventId,
					WidgetLocation.SIDE
				);
				if (!isSameEvent) {
					this.lmtStore.addLMTWidget(config as LMTConfigParams);
				}
			}
		}
	}
	@action.bound
	public removeLmtWidget(eventId: string, location: WidgetLocation): void {
		this.lmtStore.removeLMTWidget(eventId, location);
	}

	//#endregion lmt

	//#region ls

	/** Returns all active ls widgets. */
	@computed public get activeLsWidgets() {
		return this.liveStreamStore.activeWidgets;
	}

	/** Returns function that finds active ls widget on location for eventId */
	@computed public get findActiveLiveStreamWidget() {
		return this.liveStreamStore.findActiveLiveStream;
	}
	@action.bound
	public toggleLsWidget(config: TLiveStreamWidgetConfig): void {
		if (config.location === WidgetLocation.SIDE) {
			this._toggleSideWidget(config, LiveWidgetTypes.LS);
		} else {
			this.lmtStore.removeLMTWidget(config.eventId, config.location);
			this.liveStreamStore.toggleLiveStream(config);
		}

		if (
			this.liveStreamStore.findActiveLiveStream(
				config.eventId,
				WidgetLocation.SIDE
			) &&
			this.liveStreamStore.findActiveLiveStream(
				config.eventId,
				WidgetLocation.EVENT_OFFER
			)
		) {
			this.liveStreamStore.removeLiveStream(
				config.eventId,
				WidgetLocation.SIDE
			);
		}
	}

	/** Add ls widget and remove lmt widget. */
	@action.bound
	public addLiveStreamWidget(config: TLiveStreamWidgetConfig): void {
		if (
			config.location === WidgetLocation.SIDE &&
			this.activeSideWidget != null
		) {
			const prevEventId = this.activeSideWidget.eventId;

			const lsWidget = this.liveStreamStore.findActiveLiveStream(
				this.activeSideWidget.eventId,
				WidgetLocation.SIDE
			);

			if (!lsWidget) {
				this.liveStreamStore.addLiveStream(config);
			} else {
				this.liveStreamStore.updateItem(
					this.liveStreamStore.createLiveStream(config)
				);
			}

			this.lmtStore.removeLMTWidget(prevEventId, WidgetLocation.SIDE);
			return;
		}
		this.removeLmtWidget(config.eventId, config.location);
		this.liveStreamStore.addLiveStream(config);
	}

	/** Removes ls widget for event on location or any event if location is SIDE */
	@action.bound
	public removeLiveStreamWidget(
		eventId: string,
		location: WidgetLocation
	): void {
		this.liveStreamStore.removeLiveStream(eventId, location);
	}

	@action.bound
	public addEventWithLsError(eventId: string) {
		this.eventsWithLsError.add(eventId);
	}

	@action.bound
	public handleExeFeedSourceRemoved(eventId: string): void {
		const sideWidget = this.lmtStore.findActiveWidget(
			eventId,
			WidgetLocation.SIDE
		);
		const eventOfferWidget = this.lmtStore.findActiveWidget(
			eventId,
			WidgetLocation.EVENT_OFFER
		);

		[sideWidget, eventOfferWidget].forEach((widget) => {
			if (
				widget?.provider === ProviderNameEnum.ExeFeed &&
				!widget.isError
			) {
				this.lmtStore.updateItem({ ...widget, isError: true });
			}
		});
	}

	//#endregion ls

	//#region floating widget

	@observable public isWidgetFloating: boolean = false;
	public initialCss: React.CSSProperties = {};

	@action.bound
	public updateWidgetFloat(
		isFloat: boolean,
		newCss: LiveWidgetStore["initialCss"]
	): void {
		this.isWidgetFloating = isFloat;
		this.initialCss = newCss;
	}

	@action.bound
	public resetSideWidgetAutoPlay(): void {
		const widget = this.liveStreamStore.activeSideWidget;
		if (widget) {
			widget.isUserInitiated = false;
		}
	}

	//#endregion floating widget

	//#region side widget collapse

	@observable public isSideWidgetCollapsed = true;

	@action.bound
	collapseSideWidget() {
		this.isSideWidgetCollapsed = true;
	}
	@action.bound
	expandSideWidget() {
		this.isSideWidgetCollapsed = false;
	}

	@action.bound
	toggleSideWidgetCollapse() {
		this.isSideWidgetCollapsed = !this.isSideWidgetCollapsed;
	}

	//#endregion side widget collapse

	//#region disposers

	@action.bound
	public onDispose(): void {
		this.lmtStore.onDispose();
		this.liveStreamStore.onDispose();
		this.isStoreInitialized = false;
		this.eventsWithLsError.clear();
	}

	//#endregion disposers
}
